import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Gift, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { resolveImageUrl } from '@/lib/image';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GiftsPage() {
  const { category } = useParams();
  const [gifts, setGifts] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    budget_tier: 'all',
    age_range: 'all',
    search: '',
  });

  useEffect(() => {
    fetchCategoryInfo();
    fetchGifts();
  }, [filters, category]);

  const fetchCategoryInfo = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      const cat = response.data.find(c => c.slug === category);
      setCategoryInfo(cat);
    } catch (error) {
      console.error('Error fetching category:', error);
    }
  };

  const pageTitle = categoryInfo?.name || 'Gifts';
  const pageDescription = categoryInfo?.description || 'Thoughtfully curated gift recommendations';

  useEffect(() => {
    fetchCategoryInfo();
    fetchGifts();
  }, [filters, category]);

  const fetchGifts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('category', category);
      if (filters.budget_tier && filters.budget_tier !== 'all') params.append('budget_tier', filters.budget_tier);
      if (filters.age_range && filters.age_range !== 'all') params.append('age_range', filters.age_range);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/gifts?${params.toString()}`);
      setGifts(response.data);
    } catch (error) {
      console.error('Error fetching gifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setFilters({ ...filters, search: e.target.value });
  };

  return (
    <div className="page-transition py-12 px-6 md:px-12">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-primary mb-4" data-testid="gifts-page-title">
            {pageTitle}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl" data-testid="gifts-page-description">
            {pageDescription}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="font-body text-sm font-medium mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search gifts..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  className="pl-10"
                  data-testid="gifts-search-input"
                />
              </div>
            </div>
            <div>
              <label className="font-body text-sm font-medium mb-2 block">Budget</label>
              <Select value={filters.budget_tier || "all"} onValueChange={(value) => setFilters({ ...filters, budget_tier: value === "all" ? "" : value })}>
                <SelectTrigger data-testid="gifts-budget-filter">
                  <SelectValue placeholder="All Budgets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Budgets</SelectItem>
                  <SelectItem value="Under ₹1,000">Under ₹1,000</SelectItem>
                  <SelectItem value="₹1,000-2,000">₹1,000-2,000</SelectItem>
                  <SelectItem value="₹2,000-4,000">₹2,000-4,000</SelectItem>
                  <SelectItem value="₹3,000-6,000">₹3,000-6,000</SelectItem>
                  <SelectItem value="₹6,000-10,000">₹6,000-10,000</SelectItem>
                  <SelectItem value="₹8,000+">₹8,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {category === 'kids' && (
              <div>
                <label className="font-body text-sm font-medium mb-2 block">Age Range</label>
                <Select value={filters.age_range || "all"} onValueChange={(value) => setFilters({ ...filters, age_range: value === "all" ? "" : value })}>
                  <SelectTrigger data-testid="gifts-age-filter">
                    <SelectValue placeholder="All Ages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ages</SelectItem>
                    <SelectItem value="0-2 years">0-2 years</SelectItem>
                    <SelectItem value="2-4 years">2-4 years</SelectItem>
                    <SelectItem value="3-6 years">3-6 years</SelectItem>
                    <SelectItem value="6+ years">6+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Gifts Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground" data-testid="gifts-loading">Loading gifts...</p>
          </div>
        ) : gifts.length === 0 ? (
          <div className="text-center py-12">
            <Gift className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground" data-testid="gifts-empty-state">No gifts found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="gifts-grid">
            {gifts.map((gift) => (
              <Link
                key={gift.id}
                to={`/gifts/detail/${gift.id}`}
                className="group"
                data-testid={`gift-card-${gift.id}`}
              >
                <div className="bg-card border border-border/50 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden h-full">
                  <div className="relative h-64 overflow-hidden bg-muted">
                    {gift.image_url ? (
                      <img
                        src={resolveImageUrl(gift.image_url)}
                        alt={gift.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Gift className="h-16 w-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="p-6 space-y-3">
                    <div>
                      <h3 className="font-heading text-2xl font-normal text-foreground group-hover:text-primary transition-colors">
                        {gift.title}
                      </h3>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {gift.budget_tier && (
                        <span className="inline-block bg-accent/10 text-accent text-xs font-semibold px-3 py-1 rounded-full">
                          {gift.budget_tier}
                        </span>
                      )}
                      {gift.target_age_range && (
                        <span className="inline-block bg-muted text-foreground text-xs font-semibold px-3 py-1 rounded-full">
                          {gift.target_age_range}
                        </span>
                      )}
                    </div>
                    {gift.description && (
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                        {gift.description}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}